import { useLanguage } from '../utils/LanguageContext';
import { ValidatedForm, FormField, FormInput, FormSubmit, FormChoice, FormButton } from './common/ValidatedForm';
import {
  createIncomingTransferRequestClientSchema,
  createOutgoingTransferRequestClientSchema,
  createRecurringDepositClientSchema,
  MAX_WITHHOLDING_PERCENT,
} from '../shared/validations/transferValidations.js';
import ExternalAccountChooser, { renderLinkedAccount } from './ExternalAccountChooser.js';
import {
  DistributionAgeCategory,
  DistributionReason,
  DistributionReasonsByAccountTypeAndAgeCategory,
} from '../shared/constants/transferConstants.js';
import { useAuth } from '../utils/AuthContext.js';
import translations from './ComponentsTranslations.lang.json';
import { useEffect, useState } from 'react';
import { EditableValues, Explainer, ItemView, MessageRequiringConfirmation } from './common/CommonComponents.js';
import { Alert, Radio, Space } from 'antd';
import { useBooleanState, useRefreshingAsync } from './common/commonHooks.js';
import { formatAsDollars, formatAsPercent } from './common/util.js';

const getCommonTransferContext = async (api, investmentAccountId) => {
  // TODO: because the updated list of linked accounts shows up asynchronously, there's a brief moment where
  // the new linked account id chosen in the dropdown seems to be invalid. the ui will briefly show "unexpectedSystemHandled"
  // and then that error goes away. to illustrate it more, can uncomment the pause below to make the async more obvious
  // await new Promise((resolve) => setTimeout(resolve, 200));
  const investmentAccounts = (await api.getInvestmentAccounts()) ?? [];
  const investmentAccount = investmentAccounts.find((a) => a.investmentAccountId === investmentAccountId) ?? {};
  const { accountStatus, accountType, latestBalance } = investmentAccount;

  const linkedAccountIds = ((await api.getPlaidLinkedAccountsAsList()) ?? []).map(
    ({ linkedAccountId }) => linkedAccountId,
  );

  const transferOptions = await api.getUserTransferOptions();

  return { accountStatus, accountType, latestBalance, linkedAccountIds, ...transferOptions };
};

const IncomingTransferRequestForm = ({ investmentAccountId, transferRequest, submitLabel, onFinish }) => {
  const { lang } = useLanguage(translations);
  const { api } = useAuth();
  const commonContext = useRefreshingAsync(async () => await getCommonTransferContext(api, investmentAccountId));

  if (!commonContext) {
    return null;
  }

  const taxYearOptions = (commonContext.depositOptions ?? []).map((o) => ({ value: o.taxYear, label: o.taxYear }));

  const { linkedAccountId, amount, taxYear } = transferRequest ?? {};

  return (
    <ValidatedForm
      key="incomingTransferRequestForm"
      schema={createIncomingTransferRequestClientSchema}
      schemaContext={{ ...commonContext }}
      initialValues={{ linkedAccountId, amount, taxYear }}
      preValidation={({ setValue }) => {
        if (taxYearOptions.length === 1) {
          setValue('taxYear', taxYearOptions[0].value);
        }
      }}
      handleSubmit={async (values) => await api.createIncomingTransferRequest(investmentAccountId, values)}
      onFinish={onFinish}
    >
      <FormField name="linkedAccountId" render={({ field }) => <ExternalAccountChooser {...field} />} />
      <FormInput name="amount" type="dollars" step={20} />
      {taxYearOptions.length === 1 ? (
        <ItemView
          items={[
            {
              field: 'taxYear',
              value: taxYearOptions[0].value,
            },
          ]}
        />
      ) : (
        <FormChoice name="taxYear" options={taxYearOptions} type="radio" />
      )}
      <FormSubmit>{submitLabel ?? lang('createIncomingTransfer')}</FormSubmit>
    </ValidatedForm>
  );
};

const OutgoingTransferRequestForm = ({ investmentAccountId, onFinish }) => {
  const { lang, optionsForEnum } = useLanguage(translations);
  const { api } = useAuth();
  const commonContext = useRefreshingAsync(async () => getCommonTransferContext(api, investmentAccountId));
  const showWithholdingDetails = useBooleanState(false);

  if (!commonContext) {
    return null;
  }

  const { accountType, distributionAgeCategory, withholding } = commonContext;

  const initialValues = {
    federalWithholdingPercent: withholding.defaultFederalWithholdingPercent,
    stateWithholdingPercent: withholding.defaultStateWithholdingPercent,
  };

  const validDistributionReasons =
    DistributionReasonsByAccountTypeAndAgeCategory[accountType]?.[distributionAgeCategory] ?? [];

  const distributionReasonOptions = optionsForEnum(DistributionReason).filter(({ value }) =>
    validDistributionReasons.includes(value),
  );

  return (
    <ValidatedForm
      key="incomingTransferRequestForm"
      schema={createOutgoingTransferRequestClientSchema}
      schemaContext={{ ...commonContext }}
      initialValues={initialValues}
      handleSubmit={async (values) => await api.createOutgoingTransferRequest(investmentAccountId, values)}
      onFinish={onFinish}
    >
      <FormField name="linkedAccountId" render={({ field }) => <ExternalAccountChooser {...field} />} />
      <FormInput name="amount" type="dollars" step={20} />
      <FormChoice name="distributionReason" options={distributionReasonOptions} type="button" />
      <p>{lang('withholdingDescription')}</p>
      {({ watch }) =>
        showWithholdingDetails.value ? (
          <>
            <FormInput name="federalWithholdingPercent" type="percent" min={0} max={MAX_WITHHOLDING_PERCENT} />
            <FormInput name="stateWithholdingPercent" type="percent" min={0} max={MAX_WITHHOLDING_PERCENT} />
          </>
        ) : (
          <>
            <ItemView
              items={[
                {
                  field: 'transferFederalWithholdingPercent',
                  value: formatAsPercent(watch('federalWithholdingPercent')),
                },
                {
                  field: 'transferStateWithholdingPercent',
                  value: formatAsPercent(watch('stateWithholdingPercent')),
                },
              ]}
            />
            <FormButton onClick={showWithholdingDetails.setTrue}>{lang('editWithholdingButtonLabel')}</FormButton>
          </>
        )
      }
      <Explainer question={lang('withholdingExplainerQuestion')} answer={lang('withholdingExplainerAnswer')} />
      {distributionAgeCategory !== DistributionAgeCategory.NORMAL && (
        <>
          <MessageRequiringConfirmation
            title={lang('earlyWithdrawalWarningTitle')}
            content={lang('earlyWithdrawalWarningText')}
            confirmationButtonText={lang('earlyWithdrawalConfirmButtonText')}
          />
          <Alert
            type="error"
            message={lang('earlyWithdrawalWarningTitle')}
            description={lang('earlyWithdrawalWarningText')}
          />
        </>
      )}
      <FormSubmit>{lang('createOutgoingTransfer')}</FormSubmit>
    </ValidatedForm>
  );
};

const RecurringDepositView = ({ recurringDeposit }) => {
  const { api } = useAuth();
  const linkedAccounts = useRefreshingAsync(api.getPlaidLinkedAccountsAsList);
  const linkedAccount = (linkedAccounts ?? []).find((a) => a.linkedAccountId === recurringDeposit.linkedAccountId);

  const items = [
    {
      field: 'linkedAccountId',
      value: linkedAccount && renderLinkedAccount(linkedAccount),
    },
    {
      field: 'transferAmount',
      value: formatAsDollars(recurringDeposit.amount),
    },
    {
      field: 'dayOfMonth',
      value: recurringDeposit.dayOfMonth,
    },
  ];

  return <ItemView items={items} />;
};

const RecurringDepositForm = ({ investmentAccountId, recurringDeposit, submitLabel, onFinish }) => {
  const { lang } = useLanguage(translations);
  const { api } = useAuth();

  const commonContext = useRefreshingAsync(async () => getCommonTransferContext(api, investmentAccountId));

  if (!commonContext) {
    return null;
  }

  const { recurringDepositId, linkedAccountId, amount, dayOfMonth } = recurringDeposit ?? {};

  const handleSubmit = async (values) => {
    if (recurringDepositId) {
      await api.updateRecurringDeposit(investmentAccountId, { recurringDepositId, ...values });
    } else {
      await api.createRecurringDeposit(investmentAccountId, values);
    }
  };

  return (
    <ValidatedForm
      key="recurringDepositForm"
      schema={createRecurringDepositClientSchema} //works for updates too, if id is not in values
      schemaContext={{ ...commonContext, recurringDepositIds: [] }}
      initialValues={{ linkedAccountId, amount, dayOfMonth }}
      handleSubmit={handleSubmit}
      onFinish={onFinish}
    >
      <FormField name="linkedAccountId" render={({ field }) => <ExternalAccountChooser {...field} />} />
      <FormInput name="amount" type="dollars" step={20} />
      <FormInput name="dayOfMonth" type="number" min={1} max={31} />
      <FormSubmit>{submitLabel ?? lang('addRecurringDeposit')}</FormSubmit>
    </ValidatedForm>
  );
};

const RecurringDepositEditor = ({ investmentAccountId, recurringDeposit }) => {
  const { lang } = useLanguage(translations);
  const { api } = useAuth();
  const { recurringDepositId } = recurringDeposit;
  return (
    <EditableValues
      componentData={{ investmentAccountId, recurringDeposit }}
      viewComponent={RecurringDepositView}
      editComponent={RecurringDepositForm}
      onDelete={async () => await api.cancelRecurringDeposit(investmentAccountId, { recurringDepositId })}
      deleteConfirmationText={lang('deleteRecurringDepositConfirmationText')}
    />
  );
};

const OnboardingTransferForm = ({ investmentAccountId, submitLabel, onFinish }) => {
  const { lang } = useLanguage(translations);
  const { api } = useAuth();

  const [selectedOption, setSelectedOption] = useState();

  const recurringDeposits = useRefreshingAsync(
    async () => investmentAccountId && (await api.getUserRecurringDeposits(investmentAccountId)),
  );
  const transferRequests = useRefreshingAsync(
    async () => investmentAccountId && (await api.getUserTransferRequests(investmentAccountId)),
  );

  useEffect(() => {
    if (recurringDeposits && recurringDeposits.length > 0) {
      setSelectedOption('recurring');
    } else if (transferRequests && transferRequests.length > 0) {
      setSelectedOption('oneTime');
    }
  }, [recurringDeposits, transferRequests]);

  if (recurringDeposits && transferRequests) {
    const { recurringDepositId, ...recurringDeposit } = recurringDeposits[0] ?? {}; //strip out the id so it's not an edit
    const transferRequest = transferRequests[0] ?? {}; //strip out the id so it's not an edit
    return (
      <Space direction="vertical">
        <Radio.Group onChange={(e) => setSelectedOption(e.target.value)} value={selectedOption}>
          <Radio key="recurring" value="recurring">
            {lang('transferRecurring')}
          </Radio>
          <Radio key="oneTime" value="oneTime">
            {lang('transferOneTime')}
          </Radio>
        </Radio.Group>

        {selectedOption === 'recurring' && (
          <RecurringDepositForm
            investmentAccountId={investmentAccountId}
            recurringDeposit={recurringDeposit}
            submitLabel={submitLabel}
            onFinish={onFinish}
          />
        )}

        {selectedOption === 'oneTime' && (
          <IncomingTransferRequestForm
            investmentAccountId={investmentAccountId}
            transferRequest={transferRequest}
            submitLabel={submitLabel}
            onFinish={onFinish}
          />
        )}
      </Space>
    );
  }
};

export {
  IncomingTransferRequestForm,
  OutgoingTransferRequestForm,
  RecurringDepositForm,
  RecurringDepositEditor,
  OnboardingTransferForm,
};
